import React, { useEffect, useState } from "react";
import { ArrowRight, FileText, HelpCircle, ShieldCheck, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Step from "../components/Step";
import InfoCard from "../components/InfoCard";
import ExampleCard from "../components/ExampleCard";
import { apiFetch } from "../api";
import { PLAN_GENERATION_TYPES } from "../constants/planGenerationTypes";
import { getCanonicalUrl, setSeoTags } from "../utils/seo";

export default function HomePage({
  idea,
  setIdea,
  direction,
  setDirection,
  generationType,
  setGenerationType,
  city,
  setCity,
  showModal,
  setShowModal,
  onStart,
  useOwnFunds,
  setUseOwnFunds,
  ownFunds,
  setOwnFunds,
  navigate,
  user,
  onLogin,
  onAccount,
  showBlog = true,
  onShareSite,
}) {
  const [examples, setExamples] = useState([]);
  const [examplesLoading, setExamplesLoading] = useState(true);

  useEffect(() => {
    setSeoTags({
      title: "СоцПлан | Главная",
      description:
        "Онлайн-сервис генерации бизнес-планов для социального контракта. Подготовьте структуру документа и выгрузите PDF или DOCX за несколько минут.",
      url: getCanonicalUrl("/"),
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadExamples() {
      try {
        const response = await apiFetch("/examples", { silent: true });
        if (!response.ok) {
          throw new Error("Failed to load examples");
        }
        const data = await response.json();
        if (!cancelled) {
          setExamples(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setExamples([]);
        }
      } finally {
        if (!cancelled) {
          setExamplesLoading(false);
        }
      }
    }
    loadExamples();
    return () => {
      cancelled = true;
    };
  }, []);

  const featuredExamples = examples.slice(0, 6);
  const pickExample = (title, presetIdea) => {
    setDirection(title);
    setIdea(presetIdea || "");
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-6 md:py-10">
      <div className="mx-auto w-full max-w-5xl rounded-2xl border bg-white shadow-md">
        <Header
          navigate={navigate}
          showAbout
          user={user}
          onLogin={onLogin}
          onAccount={onAccount}
          showBlog={showBlog}
          onShareSite={onShareSite}
        />

        {/* Хиро‑блок */}
        <section className="px-5 md:px-6 pt-6 md:pt-8">
          <h1 className="text-[28px] leading-[1.15] md:text-[40px] font-extrabold tracking-tight text-slate-900">
            Генератор бизнес‑плана
            <br className="hidden sm:block" /> для социального контракта
          </h1>
          <p className="mt-3 text-slate-700 md:text-[17px]">
            Сформируйте черновик по типовой структуре — за минуты. Опишите идею и
            нажмите «Сгенерировать».
          </p>

          {/* Поле ввода идеи */}
          <div className="mt-4">
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              placeholder="Напр.: Небольшая кондитерская на вынос в спальном районе…"
              className="w-full rounded-lg border bg-slate-50 px-4 py-3 text-[15px] outline-none ring-0 focus:bg-white focus:border-slate-300"
              rows={2}
            />
          </div>

          {/* Кнопки */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold shadow-sm hover:bg-blue-700 active:translate-y-[1px]"
            >
              Сгенерировать бизнес‑план <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() =>
                document
                  .getElementById("how")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex h-11 items-center justify-center rounded-lg border px-4 text-[15px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Как это работает
            </button>
          </div>

          <p className="mt-2 mb-4 text-[12px] text-slate-500">
            Генерация на базе искусственного интеллекта. Структура — под требования типового
            бизнес‑плана для соцконтракта.
          </p>
        </section>

        {/* Блок: Что такое соцконтракт */}
        <section id="about" className="px-5 md:px-6">
          <h2 className="text-[20px] font-bold text-slate-900">
            Что такое социальный контракт
          </h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 h-9 w-9 flex items-center justify-center rounded-md bg-blue-50">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-[14px] text-slate-700">
                Социальный контракт — соглашение с органами соцзащиты: помощь
                деньгами и план действий, отчётность и целевое использование.
              </p>
            </div>
            <ul className="space-y-2 text-[14px] text-slate-700">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600" />
                До 350 000 ₽ на открытие бизнеса
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600" />
                До 200 000 ₽ на ЛПХ, до 30 000 ₽ на обучение
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600" />
                Подача через МФЦ/Госуслуги, требуется бизнес‑план
              </li>
            </ul>
          </div>
        </section>

        {/* Блок: Как это работает (со стрелками между пунктами) */}
        <section id="how" className="px-5 md:px-6 mt-6">
          <h2 className="text-[20px] font-bold text-slate-900">Как это работает</h2>

          {/* desktop / tablet: шаги в линию со стрелками */}
          <div className="mt-4 hidden md:flex items-center justify-between">
            <Step n={1} t="Опишите идею" />
            <ChevronRight className="h-6 w-6 text-slate-300" />
            <Step n={2} t="Получите сгенерированный план" />
            <ChevronRight className="h-6 w-6 text-slate-300" />
            <Step n={3} t="Скачайте pdf/docx(word)" />
          </div>

          {/* mobile: стопкой без стрелок справа */}
          <div className="mt-4 grid grid-cols-1 gap-3 md:hidden">
            <Step n={1} t="Опишите идею" />
            <Step n={2} t="Получите сгенерированный план" />
            <Step n={3} t="Скачайте pdf/docx(word)" />
          </div>

          <div className="mx-5 md:mx-10 my-3 h-px bg-slate-200" />
        </section>

        {/* Блок: Почему это удобно */}
        <section className="px-5 md:px-6 pb-2">
          <h2 className="text-[20px] font-bold text-slate-900">
            Почему это удобно
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <InfoCard
              icon={<FileText className="h-5 w-5 text-blue-700" />}
              title="Структура по требованиям"
              text="Разделы и поля по типовой схеме соцконтракта."
            />
            <InfoCard
              icon={<ChevronRight className="h-5 w-5 text-blue-700" />}
              title="Быстро"
              text="Черновик — за минуты после запуска."
            />
            <InfoCard
              icon={<HelpCircle className="h-5 w-5 text-blue-700" />}
              title="Экспорт после оплаты"
              text="PDF/DOCX и редактирование после покупки."
            />
          </div>
        </section>

        {/* Блок: Примеры */}
        <section id="examples" className="px-5 md:px-6 pb-6">
          <h2 className="text-[20px] font-bold text-slate-900">Примеры</h2>
          {examplesLoading ? (
            <p className="mt-4 text-sm text-slate-500">Загрузка примеров...</p>
          ) : (
            <>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {featuredExamples.map((ex) => (
                  <ExampleCard
                    key={ex.title}
                    title={ex.title}
                    imagePath={ex.imagePath}
                    onPick={() => pickExample(ex.title, ex.idea)}
                  />
                ))}
              </div>
              {featuredExamples.length === 0 && (
                <p className="mt-4 text-sm text-slate-500">Примеры появятся позже.</p>
              )}
            </>
          )}
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              onClick={() => navigate("examples")}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-6 text-white text-[15px] font-semibold shadow-sm transition-colors hover:bg-blue-700"
            >
              Больше примеров
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-6 text-white text-[15px] font-semibold shadow-sm transition-colors hover:bg-blue-700"
            >
              Создать по своей идее
            </button>
          </div>

          <div className="mt-6 border-t pt-3 pb-5 text-[12px] text-slate-500">
            <a
              href="/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-700"
            >
              Политика конфиденциальности
            </a>
            <span className="mx-2">·</span>
            <a
              href="/offer"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-700"
            >
              Оферта
            </a>
          </div>
        </section>
      </div>

      {/* Модалка генерации */}
      {showModal && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Сгенерировать бизнес‑план</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Бизнес‑направление
                </label>
                <input
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  placeholder="Напр.: Кондитерская / Парикмахерская / Груминг"
                  className="w-full rounded-lg border px-3 py-2 text-[15px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Направление генерации
                </label>
                <select
                  value={generationType}
                  onChange={(e) => setGenerationType(e.target.value)}
                  className="w-full rounded-lg border bg-white px-3 py-2 text-[15px]"
                >
                  {PLAN_GENERATION_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[12px] text-slate-500">
                  Выбор сохранится в бизнес‑плане и потом не редактируется.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">Город</label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Ваш город"
                  className="w-full rounded-lg border px-3 py-2 text-[15px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">
                  Опиши свою идею
                </label>
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-[15px] min-h-[120px]"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={useOwnFunds}
                    onChange={(e) => setUseOwnFunds(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Учитывать собственные средства в бизнес плане
                </label>
                {useOwnFunds && (
                  <input
                    type="number"
                    value={ownFunds}
                    onChange={(e) => setOwnFunds(e.target.value)}
                    placeholder="Сумма в ₽"
                    className="mt-2 w-full rounded-lg border px-3 py-2 text-[15px]"
                  />
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={onStart}
                disabled={!idea}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                Сгенерировать
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="h-11 rounded-lg border px-4 text-[15px]"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
